import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { peopleAPI } from "api/Community/PeopleAPI";
import { addressesAPI } from "api/Community/AddressesAPI";
import { Person } from "api/Community/CommunityInterfaces";

import PeopleEmptyPage from "./PeopleEmptyPage";
import GenericRecordSummary from "components/Records/RecordDisplay/GenericRecordSummary";
import BasePaper from "components/Base/BasePaper";
import GenericTabs, {
  GenericTabItem,
} from "components/Base/GenericTabs";

import PeopleProfileTab from "./PeopleDetail/PeopleProfileTab";
import PeopleReligiousInfoTab from "./PeopleDetail/PeopleReligousInfoTab";
import PeopleInteractionHistoryTabPanel from "./PeopleDetail/PeopleInteractionHistoryTab";

interface PeopleDetailProps {
  person: Person | null;
  onPeopleCreated?: (newPerson: any) => void;

  /**
   * Allows the parent page to refresh its people list or select
   * another record after this person has been deleted.
   */
  onPersonDeleted?: (deletedPersonId: number) => void;
}

type RecordActionLoading = "status" | "delete" | null;

const PeopleDetail: React.FC<PeopleDetailProps> = ({
  person,
  onPeopleCreated,
  onPersonDeleted,
}) => {
  const [data, setData] = useState<Person | null>(person);
  const [saving, setSaving] = useState(false);
  const [recordActionLoading, setRecordActionLoading] =
    useState<RecordActionLoading>(null);
  const [recordMenuOpen, setRecordMenuOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const recordMenuAnchorRef = useRef<HTMLDivElement>(null);

  // -----------------------------
  // Address selection (multiple)
  // -----------------------------
  const [addressPickerOpen, setAddressPickerOpen] = useState(false);
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const [allAddresses, setAllAddresses] = useState<any[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [pickedAddresses, setPickedAddresses] = useState<any[]>([]);

  useEffect(() => {
    setData(person);
    setTabIndex(0);
    setRecordMenuOpen(false);
    setRecordActionLoading(null);

    const existing = (person as any)?.addresses;

    if (Array.isArray(existing)) {
      setPickedAddresses(existing);
    } else {
      setPickedAddresses([]);
    }
  }, [person]);

  const handleUpdate = async (
    id: number,
    field: keyof Person,
    value: any
  ) => {
    if (!data?.id) return;

    setSaving(true);

    try {
      const payloadValue = Array.isArray((data as any)[field])
        ? (data as any)[field].join(", ")
        : value;

      const updated = await peopleAPI.update(id, {
        [field]: payloadValue,
      });

      setData((previous) => ({
        ...previous!,
        ...updated,
      }));
    } catch (error: any) {
      console.error("Error updating field:", field, error);

      alert(
        `Failed to update field "${String(field)}": ${
          error?.response?.data
            ? JSON.stringify(error.response.data)
            : error?.message || "Unknown error"
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------
  // Active / inactive status
  // -----------------------------
  const handleToggleActive = async () => {
    if (!data?.id || recordActionLoading) return;

    const currentActiveValue = (data as any).active !== false;
    const nextActiveValue = !currentActiveValue;

    setRecordActionLoading("status");

    try {
      const updated = await peopleAPI.update(data.id, {
        active: nextActiveValue,
      } as Partial<Person>);

      setData((previous) => ({
        ...previous!,
        ...updated,
        active:
          (updated as any)?.active ??
          nextActiveValue,
      }));
    } catch (error: any) {
      console.error(
        "Failed to update the person's active status:",
        error
      );

      alert(
        `Failed to ${
          currentActiveValue
            ? "mark this person as inactive"
            : "mark this person as active"
        }: ${
          error?.response?.data
            ? JSON.stringify(error.response.data)
            : error?.message || "Unknown error"
        }`
      );
    } finally {
      setRecordActionLoading(null);
    }
  };

  // -----------------------------
  // Delete person
  // -----------------------------
  const handleDeletePerson = async () => {
    if (!data?.id || recordActionLoading) return;

    const fullName = [
      (data as any).first_name,
      (data as any).last_name,
    ]
      .filter(Boolean)
      .join(" ");

    const confirmed = window.confirm(
      `Are you sure you want to permanently delete ${
        fullName || "this person"
      } from the database?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    setRecordMenuOpen(false);
    setRecordActionLoading("delete");

    try {
      const deletedPersonId = data.id;

      await peopleAPI.delete(deletedPersonId);

      setData(null);
      onPersonDeleted?.(deletedPersonId);
    } catch (error: any) {
      console.error("Failed to delete person:", error);

      alert(
        `Failed to delete this person: ${
          error?.response?.data
            ? JSON.stringify(error.response.data)
            : error?.message || "Unknown error"
        }`
      );
    } finally {
      setRecordActionLoading(null);
    }
  };

  useEffect(() => {
    if (!addressPickerOpen) return;

    let alive = true;

    setAddressesLoading(true);

    addressesAPI
      .getAll()
      .then((response: any[]) => {
        if (alive) {
          setAllAddresses(response || []);
        }
      })
      .catch((error: any) => {
        console.error("Failed to load addresses:", error);

        if (alive) {
          setAllAddresses([]);
        }
      })
      .finally(() => {
        if (alive) {
          setAddressesLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [addressPickerOpen]);

  const selectedAddressIds = useMemo(() => {
    return pickedAddresses
      .map((address: any) => address?.id)
      .filter(
        (id: any): id is number =>
          typeof id === "number"
      );
  }, [pickedAddresses]);

  const confirmAddresses = async () => {
    if (!data?.id) return;

    try {
      const updated = await peopleAPI.update(data.id, {
        address_ids: selectedAddressIds,
      });

      setData((previous) => ({
        ...previous!,
        ...updated,
      }));

      const returnedAddresses = (updated as any)?.addresses;

      if (Array.isArray(returnedAddresses)) {
        setPickedAddresses(returnedAddresses);
      }

      setAddressPickerOpen(false);
    } catch (error: any) {
      console.error("Failed to persist addresses:", error);
      alert("Failed to save addresses to this person.");
    }
  };

  const handleNewAddressSaved = async (newAddress: any) => {
    setAddAddressOpen(false);

    setPickedAddresses((previous) => {
      const existingIds = new Set(
        previous.map((address: any) => address?.id)
      );

      if (existingIds.has(newAddress?.id)) {
        return previous;
      }

      return [...previous, newAddress];
    });

    if (addressPickerOpen) {
      try {
        const response = await addressesAPI.getAll();
        setAllAddresses(response || []);
      } catch {
        // The new address remains selected even if refreshing fails.
      }
    }

    if (data?.id && newAddress?.id) {
      try {
        const nextIds = Array.from(
          new Set([...selectedAddressIds, newAddress.id])
        );

        const updated = await peopleAPI.update(data.id, {
          address_ids: nextIds,
        });

        setData((previous) => ({
          ...previous!,
          ...updated,
        }));

        const returnedAddresses = (updated as any)?.addresses;

        if (Array.isArray(returnedAddresses)) {
          setPickedAddresses(returnedAddresses);
        }
      } catch (error) {
        console.error(
          "Created address but failed to attach it:",
          error
        );
      }
    }
  };

  if (!data) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <PeopleEmptyPage
          onPeopleCreated={onPeopleCreated}
        />
      </Box>
    );
  }

  /**
   * Treat an omitted active value as true because active is true
   * by default on the backend.
   */
  const isActive = (data as any).active !== false;
  const actionLoading = recordActionLoading !== null;

  const recordActions = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "flex-end",
        width: "100%",
      }}
    >
      <ButtonGroup
        ref={recordMenuAnchorRef}
        variant="outlined"
        size="small"
        disabled={actionLoading}
        aria-label="Person record actions"
        sx={{
          "& .MuiButton-root": {
            minHeight: 28,
            py: 0.25,
            fontSize: "0.72rem",
            fontWeight: 500,
            textTransform: "none",
            whiteSpace: "nowrap",
          },
        }}
      >
        <Button
          aria-label="Show additional person actions"
          aria-controls={
            recordMenuOpen
              ? "person-record-action-menu"
              : undefined
          }
          aria-expanded={
            recordMenuOpen ? "true" : undefined
          }
          aria-haspopup="menu"
          onClick={() =>
            setRecordMenuOpen((previous) => !previous)
          }
          sx={{
            minWidth: "30px !important",
            width: 30,
            px: "2px !important",

            color: isActive
              ? "common.white"
              : "common.black",

            borderColor: isActive
              ? "rgba(255, 255, 255, 0.7)"
              : "common.white",

            backgroundColor: isActive
              ? "transparent"
              : "common.white",

            "&:hover": {
              color: isActive
                ? "common.white"
                : "common.black",

              borderColor: "common.white",

              backgroundColor: isActive
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(255, 255, 255, 0.9)",
            },

            "&.Mui-disabled": {
              color: isActive
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(0, 0, 0, 0.45)",

              borderColor: "rgba(255, 255, 255, 0.4)",

              backgroundColor: isActive
                ? "transparent"
                : "rgba(255, 255, 255, 0.6)",
            },
          }}
        >
          <ArrowDropDownIcon sx={{ fontSize: 18 }} />
        </Button>

        <Button
          onClick={handleToggleActive}
          sx={{
            minWidth: "80px !important",
            px: "10px !important",

            color: isActive
              ? "common.white"
              : "common.black",

            borderColor: isActive
              ? "rgba(255, 255, 255, 0.7)"
              : "common.white",

            backgroundColor: isActive
              ? "transparent"
              : "common.white",

            "&:hover": {
              color: isActive
                ? "common.white"
                : "common.black",

              borderColor: "common.white",

              backgroundColor: isActive
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(255, 255, 255, 0.9)",
            },

            "&.Mui-disabled": {
              color: isActive
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(0, 0, 0, 0.45)",

              borderColor: "rgba(255, 255, 255, 0.4)",

              backgroundColor: isActive
                ? "transparent"
                : "rgba(255, 255, 255, 0.6)",
            },
          }}
        >
          {recordActionLoading === "status" && (
            <CircularProgress
              size={13}
              color="inherit"
              sx={{ mr: 0.75 }}
            />
          )}

          {isActive ? "Active" : "Inactive"}
        </Button>
      </ButtonGroup>

      <Popper
        open={recordMenuOpen}
        anchorEl={recordMenuAnchorRef.current}
        placement="bottom-start"
        transition
        disablePortal
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper
              variant="outlined"
              elevation={2}
              sx={{
                mt: 0.5,
                minWidth: 170,
              }}
            >
              <ClickAwayListener
                onClickAway={() =>
                  setRecordMenuOpen(false)
                }
              >
                <MenuList
                  id="person-record-action-menu"
                  autoFocusItem
                  dense
                  sx={{ p: 0.25 }}
                >
                  <MenuItem
                    onClick={handleDeletePerson}
                    disabled={actionLoading}
                    sx={{
                      minHeight: 30,
                      px: 1,
                      py: 0.25,
                      gap: 0.75,
                      color: "error.main",
                      fontSize: "0.72rem",
                    }}
                  >
                    {recordActionLoading === "delete" ? (
                      <CircularProgress
                        size={14}
                        color="inherit"
                      />
                    ) : (
                      <DeleteOutlineIcon
                        sx={{ fontSize: 16 }}
                      />
                    )}

                    Delete from database
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );

  const tabs: GenericTabItem[] = [
    {
      id: "profile",
      label: "Profile",
      content: (
        <PeopleProfileTab
          tabIndex={0}
          index={0}
          data={data}
          saving={saving}
          onUpdate={handleUpdate}
          addressPickerOpen={addressPickerOpen}
          setAddressPickerOpen={setAddressPickerOpen}
          pickedAddresses={pickedAddresses}
          setPickedAddresses={setPickedAddresses}
          confirmAddresses={confirmAddresses}
        />
      ),
    },
    {
      id: "religious-details",
      label: "Religion",
      content: (
        <PeopleReligiousInfoTab
          tabIndex={1}
          index={1}
          data={data}
          onUpdate={handleUpdate}
          setData={setData}
        />
      ),
    },
    {
      id: "interaction-history",
      label: "Interaction History",
      content: (
        <PeopleInteractionHistoryTabPanel
          tabIndex={2}
          index={2}
          peopleId={data.id}
        />
      ),
    },
  ];

  return (
    <>
      <GenericRecordSummary
        record={data}
        titleField={["first_name", "last_name"]}
        onUpdate={handleUpdate}
        loading={saving || actionLoading}
        onClose={() => setData(null)}
        rightContent={recordActions}
      />

      <BasePaper>
        <GenericTabs
          tabs={tabs}
          value={tabIndex}
          onChange={setTabIndex}
          keepMounted
        />
      </BasePaper>
    </>
  );
};

export default PeopleDetail;